import { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/helpers";
import {
  TIMELINE_GAP,
  TIMELINE_MIN_SEGMENT,
  TIMELINE_MIN_SEGMENT_COMPACT,
  formatClock,
  formatSeconds,
  timelineWidths,
} from "./briefParts";
import { TONAL_BG } from "./BriefPrimitives";

export interface TimelineSegment {
  /** Index of the shot in the storyboard, for scrolling to its row. */
  index: number;
  /** "01", or "End" for a last shot titled as the end frame. */
  label: string;
  seconds: number;
}

/**
 * Every shot at its share of the length, never narrower than 72px (40px on a
 * phone, where a segment shows its number alone). When the floor does not
 * fit, the bar runs wider and scrolls sideways.
 */
export function BriefTimeline({
  segments,
  total,
  askedSeconds,
  complete = false,
  onSelect,
}: {
  segments: TimelineSegment[];
  total: number;
  askedSeconds?: number | null;
  /**
   * Every shot's duration parsed. The asked / over / under fragment compares
   * the whole length, so it shows only then; a partial total would be wrong.
   */
  complete?: boolean;
  onSelect: (index: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      setWidth(Math.floor(entries[0]?.contentRect.width ?? 0));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // A phone's track (Pen j8tKA) floors at 40 and labels by number alone.
  const compact = width > 0 && width < 560;
  const floor = compact ? TIMELINE_MIN_SEGMENT_COMPACT : TIMELINE_MIN_SEGMENT;
  const widths = width > 0 ? timelineWidths(segments.map((s) => s.seconds), width, floor) : null;
  const rowWidth = widths
    ? widths.reduce((sum, w) => sum + w, 0) + TIMELINE_GAP * Math.max(0, segments.length - 1)
    : null;
  const roundedTotal = Math.round(total * 10) / 10;
  const asked = complete && askedSeconds && askedSeconds > 0 ? askedSeconds : null;
  const diff = asked ? Math.round((roundedTotal - asked) * 10) / 10 : 0;

  return (
    <div>
      <p className="flex flex-wrap items-baseline gap-x-2 text-[15px] leading-snug">
        <span className="font-semibold text-co-text">{formatSeconds(roundedTotal)}</span>
        {asked ? <span className="text-co-text2">of the {asked} s the idea asked for</span> : null}
        {asked && diff !== 0 ? <span className="text-co-muted" aria-hidden="true">·</span> : null}
        {asked && diff > 0 ? <span className="text-co-alert-ink">{diff} s over</span> : null}
        {asked && diff < 0 ? <span className="text-co-muted">{Math.abs(diff)} s under</span> : null}
      </p>
      <div ref={trackRef} className="mt-2.5 overflow-x-auto">
        <div
          className="flex"
          style={{ gap: TIMELINE_GAP, width: rowWidth ?? undefined, minWidth: rowWidth ? undefined : "100%" }}
        >
          {segments.map((segment, i) => (
            <button
              key={segment.index}
              type="button"
              onClick={() => onSelect(segment.index)}
              title={`Go to shot ${segment.label}`}
              className={cn(
                "flex h-[30px] shrink-0 items-center overflow-hidden rounded-tag text-left text-[12px] text-co-text2 transition-colors hover:bg-co-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-co-accent",
                TONAL_BG,
                compact ? "px-2" : "px-[10px]",
              )}
              style={
                widths
                  ? { width: widths[i] }
                  : { flex: `${segment.seconds} 1 0`, minWidth: floor }
              }
            >
              <span className="truncate">
                {compact ? segment.label : `${segment.label} · ${formatSeconds(segment.seconds)}`}
              </span>
            </button>
          ))}
        </div>
        <div
          className="mt-2.5 flex justify-between text-[12px] text-co-muted"
          style={{ width: rowWidth ?? undefined }}
        >
          <span>0:00</span>
          <span>{formatClock(total)}</span>
        </div>
      </div>
    </div>
  );
}
